import { useEffect, useMemo, useState } from 'react';
import { API_URL as API } from '../../config/env.js';
import { adminHeaders, adminAuthErrorMessage } from '../../utils/adminAuth.js';

function formatMoney(paise) {
  return `₹${(Number(paise || 0) / 100).toLocaleString('en-IN')}`;
}

function formatWhen(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const STATUS_FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'paid', label: 'Paid' },
  { id: 'pending', label: 'Pending' },
  { id: 'refunded', label: 'Refunded' },
];

function statusClass(status) {
  if (status === 'paid') return 'paid';
  if (status === 'refunded') return 'expired';
  return 'free';
}

export default function AdminPayments({ token, onLogout, showToast }) {
  const [data, setData] = useState({ stats: null, payments: [] });
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('all');
  const [search, setSearch] = useState('');
  const [refundingId, setRefundingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/payments`, { headers: adminHeaders(token) });
      const json = await res.json().catch(() => ({}));
      if (res.status === 401) return onLogout(adminAuthErrorMessage(res, json));
      if (!res.ok) return showToast(json.error || 'Could not load payments', 'error');
      setData({ stats: json.stats, payments: json.payments || [] });
    } catch {
      showToast('Network error loading payments', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [token]);

  const counts = useMemo(() => {
    const c = { all: 0, paid: 0, pending: 0, refunded: 0 };
    for (const p of data.payments) {
      c.all += 1;
      const s = String(p.status || '').toLowerCase();
      if (s === 'paid') c.paid += 1;
      else if (s === 'refunded') c.refunded += 1;
      else c.pending += 1;
    }
    return c;
  }, [data.payments]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return data.payments.filter((p) => {
      const s = String(p.status || '').toLowerCase();
      if (tab === 'paid' && s !== 'paid') return false;
      if (tab === 'refunded' && s !== 'refunded') return false;
      if (tab === 'pending' && (s === 'paid' || s === 'refunded')) return false;
      if (!q) return true;
      const hay = `${p.phone || ''} ${p.order_id || ''} ${p.payment_id || ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [data.payments, tab, search]);

  const markRefunded = async (id) => {
    if (!window.confirm('Mark this payment as refunded? (Do this after refunding in Razorpay.)')) return;
    setRefundingId(id);
    try {
      const res = await fetch(`${API}/api/admin/payments/${id}/refund`, {
        method: 'PUT',
        headers: adminHeaders(token),
      });
      if (res.ok) {
        showToast('Marked as refunded');
        load();
      } else {
        showToast('Could not update', 'error');
      }
    } catch {
      showToast('Could not update', 'error');
    }
    setRefundingId(null);
  };

  return (
    <div className="adm-panel adm-pay">
      <div className="adm-panel__toolbar">
        <p className="adm-panel__note">
          Razorpay order log — amounts, status, and IDs for support and refunds.
        </p>
        <button type="button" className="adm-btn adm-btn-ghost" onClick={load} disabled={loading}>
          Refresh
        </button>
      </div>

      <div className="adm-kpi-grid">
        <div className="adm-kpi adm-kpi--accent">
          <span className="adm-kpi__label">Confirmed revenue</span>
          <span className="adm-kpi__value">{formatMoney(data.stats?.revenuePaise)}</span>
          <span className="adm-kpi__sub">{data.stats?.paid ?? 0} successful payments</span>
        </div>
        <div className="adm-kpi">
          <span className="adm-kpi__label">Pending / failed</span>
          <span className="adm-kpi__value">{data.stats?.pending ?? '—'}</span>
          <span className="adm-kpi__sub">Not completed</span>
        </div>
        <div className="adm-kpi">
          <span className="adm-kpi__label">Refunded</span>
          <span className="adm-kpi__value">{data.stats?.refunded ?? '—'}</span>
          <span className="adm-kpi__sub">Marked in admin</span>
        </div>
      </div>

      <section className="adm-panel__body">
        <header className="adm-panel__section-head">
          <div>
            <h3>Payment log</h3>
            <p>Search by email, phone, order ID, or payment ID. Mark refunded after you process it in Razorpay.</p>
          </div>
        </header>

        <div className="adm-panel__filters">
          <input
            type="search"
            className="adm-panel__search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search account, order, or payment ID…"
          />
          <div className="adm-seg" role="tablist" aria-label="Payment status">
            {STATUS_FILTERS.map((f) => (
              <button
                key={f.id}
                type="button"
                role="tab"
                aria-selected={tab === f.id}
                className={`adm-seg__btn ${tab === f.id ? 'active' : ''}`}
                onClick={() => setTab(f.id)}
              >
                {f.label}
                <em>{counts[f.id]}</em>
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="adm-empty">Loading payments…</div>
        ) : visible.length === 0 ? (
          <div className="adm-empty">No payments in this list.</div>
        ) : (
          <ul className="adm-pay-list">
            {visible.map((p) => {
              const s = String(p.status || '—');
              return (
                <li key={p.id} className="adm-pay-card">
                  <div className="adm-pay-card__main">
                    <div className="adm-pay-card__amount">{formatMoney(p.amount)}</div>
                    <div className="adm-pay-card__meta">
                      <div className="adm-pay-card__account">{p.phone || 'Unknown account'}</div>
                      <div className="adm-pay-card__when">{formatWhen(p.created_at)}</div>
                    </div>
                    <span className={`adm-status adm-status--${statusClass(s)}`}>{s}</span>
                  </div>

                  <div className="adm-pay-card__ids">
                    <div>
                      <span>Order</span>
                      <code>{p.order_id || '—'}</code>
                    </div>
                    <div>
                      <span>Payment</span>
                      <code>{p.payment_id || '—'}</code>
                    </div>
                  </div>

                  {s === 'paid' && (
                    <div className="adm-pay-card__actions">
                      <button
                        type="button"
                        className="adm-btn adm-btn-ghost adm-btn-sm"
                        disabled={refundingId === p.id}
                        onClick={() => markRefunded(p.id)}
                      >
                        {refundingId === p.id ? 'Updating…' : 'Mark refunded'}
                      </button>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
}
